<template>
  <div>
    <div class="sm:p-8 p-4 bg-blue-100 rounded-md sm:grid sm:grid-cols-2 max-sm:flex max-sm:flex-col gap-x-8 gap-y-4">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block font-medium sm:col-start-1 sm:col-end-3  text-md font-medium text-gray-700">
            Select Campaign
          </label>
          <DropdownComponent
            v-show="videoImpressionsHasLoadedCampaignData"
            :items="combinedAllCampaignsAndAllIntegrations"
            display="name"
            :selected="currentlySelectedSuperCampaign ? currentlySelectedSuperCampaign.name : 'Select a Campaign'"
            :key="combinedCampaignSelectorRefresher"
            @user_select="setCurrentSuperCampaign($event)"
          />
        </div>

        <div v-show="currentlySelectedSuperCampaign">
          <label class="block font-medium sm:col-start-1 sm:col-end-3  text-md font-medium text-gray-700">Date Range</label>
          <VueDatePicker
            class="pt-1"
            v-model="interval.userSelectionRange"
            range
            :min-date="intervalMinMax.minMax.min"
            :max-date="intervalMinMax.minMax.max"
            :enable-time-picker="false"
            prevent-min-max-navigation
            :preset-ranges="presetRanges"
            :clearable="false"
          >
            <template #yearly="{ label, range, presetDateRange }">
              <span @click="presetDateRange(range)">{{ label }}</span>
            </template>
          </VueDatePicker>
        </div>
      </div>

      <div class="grid-container gap-2" v-if="showOverviewPanel">
        <div class="card-container" v-if="overviewTotalEngagements > 0">
          <div class="inner-card">
            <label> Total Engagements: </label>
            <span> {{ overviewTotalEngagementsFormatted }} </span>
          </div>
        </div>
        <div class="card-container" v-if="overviewTotalImpressions > 0">
          <div class="inner-card">
            <label> Total Impressions </label>
            <span> {{ formatNumber(overviewTotalImpressions) }} </span>
          </div>
        </div>
        <div class="card-container" v-if="overviewTotalVideoImpressions > 0">
          <div class="inner-card">
            <label> Total Video Impressions </label>
            <span> {{ formatNumber(overviewTotalVideoImpressions) }} </span>
          </div>
        </div>
      </div>
    </div>

    <TabButtons
      v-if="currentlySelectedSuperCampaign"
      :buttons="panelSwitchingButtons"
      :active_button="currentPanel"
      :panel-changed-method="panelChanged"
    />

    <!-- Always mount all children; toggle visibility with v-show to avoid race conditions -->
    <keep-alive>
      <NativeDisplayReporting
        v-show="currentPanel === 'NativeDisplayReporting' && currentlySelectedCampaign"
        :current_campaign="currentlySelectedCampaign"
        :userSelectionRange="interval.userSelectionRange"
        @isActiveFlag="updatePanelIsActive"
        @min-max-range-array="setNativeDisplayReportIntervalMinMaxArray"
        @impression-total="overviewTotalImpressions = $event"
        @is-reward-video="updateIsRewardVideoFlag"
      />

      <IntegrationsReporting
        v-show="currentPanel === 'IntegrationsReporting' && currentlySelectedIntegration"
        :all_integrations="allIntegrations"
        :current_campaign="currentlySelectedCampaign"
        :current_integration="currentlySelectedIntegration"
        :userSelectionRangeProp="interval.userSelectionRange"
        @isActiveFlag="updatePanelIsActive"
        @min-max-range-array="setIntegrationsReportIntervalMinMaxArray"
        @total-engagements="overviewTotalEngagements = $event"
      />

      <VideoImpressionsReporting
        v-show="currentPanel === 'VideoImpressionsReporting' && currentlySelectedCampaign"
        :current_campaign="currentlySelectedCampaign"
        :userSelectionRange="interval.userSelectionRange"
        @isActiveFlag="updatePanelIsActive"
        @total-video-impressions="overviewTotalVideoImpressions = $event"
        @has-loaded-data="videoImpressionsHasLoadedCampaignData = $event"
      />
    </keep-alive>
  </div>
</template>

<script>
import NativeDisplayReporting from '@/components/CombinedReporting/NativeDisplayReportingComponent.vue'
import IntegrationsReporting from '@/components/CombinedReporting/IntegrationsReportingComponent.vue'
import VideoImpressionsReporting from '@/components/CombinedReporting/VideoImpressionsReportingComponent.vue'
import TabButtons from '@/components/TabButtons.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { subDays } from 'date-fns'

export default {
  name: 'CombinedReporting',
  components: {
    NativeDisplayReporting,
    IntegrationsReporting,
    VideoImpressionsReporting,
    TabButtons,
    VueDatePicker
  },
  props: {},
  data() {
    return {
      interval: {
        minMax: {
          min: null,
          max: null
        },
        userSelectionRange: [null, null]
      },
      videoImpressionsHasLoadedCampaignData: false,
      nativeDisplayIsRewardVideo: false,
      currentPanel: 'IntegrationsReporting',
      allCampaigns: [],
      allIntegrations: [],
      integrationsReportIntervalMinMaxArray: [],
      nativeDisplayReportIntervalMinMaxArray: [],
      overviewTotalImpressions: 0,
      overviewTotalVideoImpressions: 0,
      overviewTotalEngagements: 0,
      currentlySelectedCampaign: null,
      currentlySelectedIntegration: null,
      currentlySelectedSuperCampaign: null,       // added (used in template/logic)
      campaignSelectorRefresher: 0,
      integrationSelectorRefresher: 0,
      combinedCampaignSelectorRefresher: 0,       // added (used as :key)
      panelSwitchingButtonsData: [
        { name: 'IntegrationsReporting', text: 'Integrations Reporting', panelIsActive: false },
        { name: 'NativeDisplayReporting', text: 'Native Display Reporting', panelIsActive: false },
        { name: 'VideoImpressionsReporting', text: 'Video Impressions Reporting', panelIsActive: false }
      ]
    }
  },
  computed: {
    panelSwitchingButtons() {
      return this.panelSwitchingButtonsData
        .filter(p => p.panelIsActive)
        .map(p => {
          if (p.name === 'NativeDisplayReporting') {
            return {
              ...p,
              text: this.nativeDisplayIsRewardVideo ? 'Reward Video Reporting' : 'Native Display Reporting'
            }
          }
          return p
        })
    },



    combinedAllCampaignsAndAllIntegrations() {
      const modifiedIntegrations = this.allIntegrations.map(integration => {
        const { integration_friendly_name, ...rest } = integration
        return { name: integration_friendly_name, ...rest }
      })

      let combined = [...this.allCampaigns, ...modifiedIntegrations]

      combined = combined.filter(item => {
        if (!item.campaign_uuid) return true
        return !this.allCampaigns.some(c => c.uuid === item.campaign_uuid)
      })

      const safeStart = x => {
        const d = x && x.start_date ? new Date(x.start_date) : null
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : new Date(0)
      }

      combined.sort((a, b) => safeStart(b) - safeStart(a))
      return combined
    },

    intervalMinMax() {
      const defaultMinMax = {
        min: new Date('2000-01-01'),
        max: new Date('2100-01-01')
      }

      const allDates = [
        ...this.integrationsReportIntervalMinMaxArray,
        ...this.nativeDisplayReportIntervalMinMaxArray
      ].filter(d => d instanceof Date && !Number.isNaN(d.getTime()))

      if (allDates.length === 0) return { minMax: defaultMinMax }

      const minDate = new Date(Math.min(...allDates.map(d => d.getTime())))
      const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())))
      return { minMax: { min: minDate, max: maxDate } }
    },

    presetRanges() {
      return [
        { label: 'Last 30 days', range: [subDays(new Date(), 31), subDays(new Date(), 1)] },
        { label: 'Last 60 days', range: [subDays(new Date(), 61), subDays(new Date(), 1)] },
        { label: 'Last 90 days', range: [subDays(new Date(), 91), subDays(new Date(), 1)] },
        { label: 'All time', range: [this.intervalMinMax.minMax.min, this.intervalMinMax.minMax.max] }
      ]
    },

    overviewTotalEngagementsFormatted() {
      if (!this.overviewTotalEngagements) return 0
      return this.formatNumber(this.overviewTotalEngagements)
    },

    showOverviewPanel() {
      return (
        this.overviewTotalImpressions > 0 ||
        this.overviewTotalVideoImpressions > 0 ||
        this.overviewTotalEngagements > 0
      )
    }
  },
  watch: {
    panelSwitchingButtons(newButtons) {
      if (newButtons.length === 1) {
        this.currentPanel = newButtons[0].name
        return
      }
      const integrationsPref = newButtons.find(
        b => b.name === 'IntegrationsReporting' && b.panelIsActive
      )
      if (integrationsPref) this.currentPanel = 'IntegrationsReporting'
    },

    intervalMinMax(mm) {
      this.interval.userSelectionRange = [mm.minMax.min, mm.minMax.max]
    }
  },
  created() {
    this.getAllCampaigns()
    this.getAllIntegrations()
  },
  mounted() {},
  methods: {
    updateIsRewardVideoFlag(isRewardVideo) {
      this.nativeDisplayIsRewardVideo = !!isRewardVideo
    },

    panelChanged(panelName) {
      this.currentPanel = panelName
    },

    updatePanelIsActive(panelName, isActive) {
      const panel = this.panelSwitchingButtonsData.find(p => p.name === panelName)
      if (!panel) return
      panel.panelIsActive = !!isActive

      const activeNames = this.panelSwitchingButtons
        .filter(p => p.panelIsActive)
        .map(p => p.name)

      if (!activeNames.includes(this.currentPanel) && activeNames.length) {
        this.currentPanel = activeNames.includes('IntegrationsReporting')
          ? 'IntegrationsReporting'
          : activeNames[0]
      }
    },

    resetAllPanelsHaveData() {
      this.panelSwitchingButtonsData.forEach(p => (p.panelIsActive = false))
    },

    setCurrentSuperCampaign(campaignOrIntegration) {
      if (this.currentlySelectedSuperCampaign && campaignOrIntegration.uuid === this.currentlySelectedSuperCampaign.uuid) return

      this.integrationsReportIntervalMinMaxArray = []
      this.nativeDisplayReportIntervalMinMaxArray = []
      this.interval.userSelectionRange = [null, null]
      this.interval.minMax = { min: null, max: null } // fixed key (was minmax)

      this.currentlySelectedCampaign = null
      this.currentlySelectedIntegration = null

      if ('name' in campaignOrIntegration && 'uuid' in campaignOrIntegration) {
        this.setCurrentCampaign(campaignOrIntegration)
      } else if ('event_id_05' in campaignOrIntegration) {
        this.setCurrentIntegration(campaignOrIntegration)
      }

      this.currentlySelectedSuperCampaign = campaignOrIntegration
      this.resetAllPanelsHaveData()
      this.resetOverviewTotals()
      this.nativeDisplayIsRewardVideo = false

      this.primePanelsAfterSelect()
    },

    resetOverviewTotals() {
      this.overviewTotalImpressions = 0
      this.overviewTotalVideoImpressions = 0
      this.overviewTotalEngagements = 0
    },

    setCurrentCampaign(campaign) {
      this.currentlySelectedCampaign = campaign

      const matchingIntegration = this.allIntegrations.find(
        i => i.campaign_uuid === this.currentlySelectedCampaign.uuid
      )

      if (matchingIntegration && this.currentlySelectedIntegration?.event_id_05 !== matchingIntegration.event_id_05) {
        this.setCurrentIntegration(matchingIntegration)
      } else {
        this.currentlySelectedIntegration = null
      }

      this.campaignSelectorRefresher++
      this.combinedCampaignSelectorRefresher++
    },

    setCurrentIntegration(integration) {
      this.currentlySelectedIntegration = integration

      const matchingCampaign = this.allCampaigns.find(c => c.uuid === this.currentlySelectedIntegration.campaign_uuid)
      if (matchingCampaign && this.currentlySelectedCampaign?.uuid !== matchingCampaign.uuid) {
        this.setCurrentCampaign(matchingCampaign)
      }

      this.integrationSelectorRefresher++
    },

    primePanelsAfterSelect() {
      this.setPanelActive('IntegrationsReporting', !!this.currentlySelectedIntegration)
      this.setPanelActive('NativeDisplayReporting', !!this.currentlySelectedCampaign)
      this.setPanelActive('VideoImpressionsReporting', !!this.currentlySelectedCampaign)

      if (
        this.currentPanel &&
        this.panelSwitchingButtons.some(b => b.name === this.currentPanel && b.panelIsActive)
      ) {
        return
      }

      if (this.currentlySelectedIntegration) this.currentPanel = 'IntegrationsReporting'
      else if (this.currentlySelectedCampaign) this.currentPanel = 'NativeDisplayReporting'
      else this.currentPanel = 'IntegrationsReporting'
    },

    setPanelActive(name, isActive) {
      const p = this.panelSwitchingButtonsData.find(x => x.name === name)
      if (p) p.panelIsActive = !!isActive
    },

    async getAllCampaigns() {
      try {
        const { data } = await this.$http.get('/campaigns/get/all', {
          params: {
            un_archived_only: true
          }
        })
        this.allCampaigns = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('getAllCampaigns failed', e)
        this.allCampaigns = []
      }
    },

    async getAllIntegrations() {
      try {
        const { data } = await this.$http.get('/integrations')
        this.allIntegrations = data?.data ?? []
      } catch (e) {
        console.error('getAllIntegrations failed', e)
        this.allIntegrations = []
      }
    },

    setIntegrationsReportIntervalMinMaxArray(minMaxArray) {
      this.integrationsReportIntervalMinMaxArray = minMaxArray
      this.interval.userSelectionRange = [this.intervalMinMax.minMax.min, this.intervalMinMax.minMax.max]
    },

    setNativeDisplayReportIntervalMinMaxArray(minMaxArray) {
      this.nativeDisplayReportIntervalMinMaxArray = minMaxArray
      this.interval.userSelectionRange = [this.intervalMinMax.minMax.min, this.intervalMinMax.minMax.max]
    },

    formatNumber(value, locales = 'en-US', options = {}) {
      const defaultOptions = { maximumFractionDigits: 2, ...options }
      return new Intl.NumberFormat(locales, defaultOptions).format(value ?? 0)
    }
  }
}
</script>

<style lang="postcss" scoped>
:deep .dp__action_select {
  color: white;
  background-color: green;
  opacity: 1 !important;
  border: 1px solid black;
  padding: 0.8em 0.4em;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep .dp__action_cancel {
  color: white;
  background-color: red;
  opacity: 1 !important;
  border: 1px solid black;
  padding: 0.8em 0.4em;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.dp__action_button:hover) {
  color: white;
  background-color: blue;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.card-container {
  @apply bg-white rounded-md flex flex-col items-center justify-center p-4 gap-4;
}

.date-picker {
  @apply lg:max-w-lg sm:max-w-xs w-full block shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md;
}

.inner-card {
  @apply flex flex-col gap-2 items-center;
}

.card-container span {
  @apply text-3xl font-bold;
}
</style>